import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Database, History, Settings, ShieldAlert, Trash2 } from 'lucide-react';
import type { Event, EventVersionSummary } from '@wht-xc-utils/shared/domain';

import { CoordinatorInput } from '../components/CoordinatorInput';
import { EventTabs } from '../components/EventTabs';
import { api } from '../lib/api';

interface HistoryResponse {
  versions: EventVersionSummary[];
  latestVersion: number | null;
}

export function EventAdministrationLayout() {
  const { eventId } = useParams<{ eventId: string }>();
  return (
    <>
      <EventTabs />
      <section className="admin-shell">
        <header className="admin-heading">
          <div>
            <span className="eyebrow">Event administration</span>
            <h2>Settings and recovery</h2>
          </div>
          <Settings aria-hidden="true" />
        </header>
        <nav className="admin-nav" aria-label="Event administration">
          <NavLink end to={`/events/${eventId}/admin`}>
            Overview
          </NavLink>
          <NavLink to={`/events/${eventId}/admin/settings`}>Settings</NavLink>
          <NavLink to={`/events/${eventId}/admin/history`}>Version history</NavLink>
          <NavLink to={`/events/${eventId}/admin/data`}>Data management</NavLink>
        </nav>
        <Outlet />
      </section>
    </>
  );
}

export function EventAdministrationHome() {
  const { eventId } = useParams<{ eventId: string }>();
  return (
    <div className="admin-card-grid">
      <AdminCard
        icon={Settings}
        title="Event settings"
        description="Packet instructions, coordinator names, and score-sheet configuration."
        to={`/events/${eventId}/admin/settings`}
      />
      <AdminCard
        icon={History}
        title="Version history"
        description="Review every recorded operation and restore an earlier event state."
        to={`/events/${eventId}/admin/history`}
      />
      <AdminCard
        icon={Database}
        title="Data management"
        description="Duplicate this workspace or permanently delete the event."
        to={`/events/${eventId}/admin/data`}
      />
    </div>
  );
}

export function EventAdministrationSettings() {
  const { eventId } = useParams<{ eventId: string }>();
  const queryClient = useQueryClient();
  const [scoreSheetRows, setScoreSheetRows] = useState(20);
  const [coordinators, setCoordinators] = useState<string[]>([]);
  const [packetInstructions, setPacketInstructions] = useState('');
  const [stoppingPointInstructions, setStoppingPointInstructions] = useState('');
  const [timingPointInstructions, setTimingPointInstructions] = useState('');
  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<{ event: Event }>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });
  const updateSettings = useMutation({
    mutationFn: (input: {
      scoreSheetRows: number;
      coordinators: string[];
      packetInstructions: string[];
      stoppingPointInstructions: string[];
      timingPointInstructions: string[];
    }) => api.patch<{ event: Event }>(`/api/events/${eventId}`, input),
    onSuccess: ({ event: updatedEvent }) => {
      queryClient.setQueryData(['event', eventId], { event: updatedEvent });
      queryClient.invalidateQueries({ queryKey: ['eventHistory', eventId] });
    },
  });

  useEffect(() => {
    if (!event.data?.event) return;
    setScoreSheetRows(event.data.event.scoreSheetRows);
    setCoordinators(event.data.event.coordinators);
    setPacketInstructions(event.data.event.packetInstructions.join('\n'));
    setStoppingPointInstructions(event.data.event.stoppingPointInstructions.join('\n'));
    setTimingPointInstructions(event.data.event.timingPointInstructions.join('\n'));
  }, [event.data?.event]);

  return (
    <div className="card event-settings-card">
      <div>
        <span className="eyebrow">Packet settings</span>
        <h2>Event details</h2>
        <p className="muted">Enter one packet instruction per line.</p>
      </div>
      <div className="form-grid">
        <label htmlFor="event-score-sheet-rows">Score sheet rows</label>
        <input
          id="event-score-sheet-rows"
          type="number"
          min={1}
          value={scoreSheetRows}
          onChange={(event) => setScoreSheetRows(Number(event.target.value) || 20)}
        />
        <label htmlFor="event-coordinators">Coordinators</label>
        <CoordinatorInput
          id="event-coordinators"
          value={coordinators}
          onChange={setCoordinators}
          disabled={updateSettings.isPending}
        />
        <label htmlFor="event-packet-instructions">General instructions</label>
        <textarea
          id="event-packet-instructions"
          rows={10}
          value={packetInstructions}
          onChange={(event) => setPacketInstructions(event.target.value)}
        />
        <label htmlFor="event-stopping-instructions">Stopping point instructions</label>
        <textarea
          id="event-stopping-instructions"
          rows={4}
          value={stoppingPointInstructions}
          onChange={(event) => setStoppingPointInstructions(event.target.value)}
        />
        <label htmlFor="event-timing-instructions">Timing point instructions</label>
        <textarea
          id="event-timing-instructions"
          rows={4}
          value={timingPointInstructions}
          onChange={(event) => setTimingPointInstructions(event.target.value)}
        />
      </div>
      <div className="toolbar">
        <button
          className="primary"
          disabled={updateSettings.isPending}
          onClick={() =>
            updateSettings.mutate({
              scoreSheetRows,
              coordinators,
              packetInstructions: parseInstructionLines(packetInstructions),
              stoppingPointInstructions: parseInstructionLines(stoppingPointInstructions),
              timingPointInstructions: parseInstructionLines(timingPointInstructions),
            })
          }
        >
          {updateSettings.isPending ? 'Saving…' : 'Save settings'}
        </button>
        {updateSettings.isSuccess ? <span className="success">Saved</span> : null}
        {updateSettings.error ? (
          <span className="error">{(updateSettings.error as Error).message}</span>
        ) : null}
      </div>
    </div>
  );
}

export function EventHistoryPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [beforeVersion, setBeforeVersion] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<EventVersionSummary[]>([]);
  const history = useQuery({
    queryKey: ['eventHistory', eventId, beforeVersion],
    queryFn: () =>
      api.get<HistoryResponse>(
        `/api/events/${eventId}/history?limit=25${
          beforeVersion == null ? '' : `&beforeVersion=${beforeVersion}`
        }`,
      ),
    enabled: !!eventId,
  });

  useEffect(() => {
    if (!history.data) return;
    setLoaded((current) => {
      const combined =
        beforeVersion == null ? history.data.versions : [...current, ...history.data.versions];
      return [...new Map(combined.map((version) => [version.id, version])).values()];
    });
  }, [beforeVersion, history.data]);

  const versions = beforeVersion == null && history.data ? history.data.versions : loaded;
  const nextVersion =
    history.data?.versions.length === 25
      ? (history.data.versions.at(-1)?.versionNumber ?? null)
      : null;

  return (
    <div className="card">
      <span className="eyebrow">Recovery timeline</span>
      <h2>Version history</h2>
      <p className="muted">
        Each entry represents one completed operation. Shared volunteer-profile experience is not
        changed by event reverts.
      </p>
      {history.isLoading && versions.length === 0 ? (
        <p className="muted">Loading history…</p>
      ) : null}
      <div className="history-list">
        {versions.map((version) => (
          <Link
            className="history-entry"
            key={version.id}
            to={`/events/${eventId}/admin/history/${version.id}`}
          >
            <span className="history-version">v{version.versionNumber}</span>
            <span>
              <strong>{version.summary}</strong>
              <small>
                {new Date(version.createdAt).toLocaleString()} · {version.operationSource}
              </small>
            </span>
            <span className="history-count">{changeCount(version)} changes</span>
          </Link>
        ))}
      </div>
      {nextVersion != null ? (
        <button
          className="subtle"
          disabled={history.isFetching}
          onClick={() => setBeforeVersion(nextVersion)}
        >
          {history.isFetching ? 'Loading…' : 'Load older versions'}
        </button>
      ) : null}
    </div>
  );
}

export function EventHistoryDetailPage() {
  const { eventId, versionId } = useParams<{
    eventId: string;
    versionId: string;
  }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: ['eventHistoryVersion', eventId, versionId],
    queryFn: () =>
      api.get<{ version: EventVersionSummary }>(`/api/events/${eventId}/history/${versionId}`),
    enabled: !!eventId && !!versionId,
  });
  const latest = useQuery({
    queryKey: ['eventHistory', eventId, 'latest'],
    queryFn: () => api.get<HistoryResponse>(`/api/events/${eventId}/history?limit=1`),
    enabled: !!eventId,
  });
  const revert = useMutation({
    mutationFn: () =>
      api.post<{ version: EventVersionSummary }>(
        `/api/events/${eventId}/history/${versionId}/revert`,
        { expectedCurrentVersion: latest.data?.latestVersion },
      ),
    onSuccess: ({ version }) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.some((value) => value === eventId),
      });
      navigate(`/events/${eventId}/admin/history/${version.id}`);
    },
  });
  const version = detail.data?.version;
  const diffEntries = useMemo(() => Object.entries(version?.diff ?? {}), [version]);
  return (
    <div className="card">
      <Link to={`/events/${eventId}/admin/history`}>← Version history</Link>
      {version ? (
        <>
          <span className="eyebrow">Version {version.versionNumber}</span>
          <h2>{version.summary}</h2>
          <p className="muted">
            {new Date(version.createdAt).toLocaleString()} · {version.operationSource} ·{' '}
            {version.operationKey}
          </p>
          <table>
            <thead>
              <tr>
                <th>Area</th>
                <th>Added</th>
                <th>Updated</th>
                <th>Deleted</th>
              </tr>
            </thead>
            <tbody>
              {diffEntries.map(([name, diff]) => (
                <tr key={name}>
                  <td>{humanize(name)}</td>
                  <td>{diff.added}</td>
                  <td>{diff.updated}</td>
                  <td>{diff.deleted}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="notice warning">
            Reverting replaces the complete event-owned state. Shared volunteer-profile experience
            is not reverted.
          </div>
          <button
            className="danger"
            disabled={
              revert.isPending ||
              latest.data?.latestVersion == null ||
              latest.data.latestVersion === version.versionNumber
            }
            onClick={() => {
              if (
                confirm(
                  `Revert the entire event to version ${version.versionNumber}? The current state will remain available in history.`,
                )
              ) {
                revert.mutate();
              }
            }}
          >
            <History aria-hidden="true" />
            {revert.isPending ? 'Reverting…' : 'Revert entire event to this version'}
          </button>
          {revert.error ? <p className="error">{(revert.error as Error).message}</p> : null}
        </>
      ) : (
        <p className="muted">Loading version…</p>
      )}
    </div>
  );
}

export function EventDataManagementPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<{ event: Event }>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });
  const copyEvent = useMutation({
    mutationFn: () =>
      api.post<{ event: Event }>(`/api/events/${eventId}/copy`, {
        name: `${event.data?.event.name ?? 'Event'} (Copy)`,
      }),
    onSuccess: ({ event: copied }) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate(`/events/${copied.id}`);
    },
  });
  const deleteEvent = useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/api/events/${eventId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/');
    },
  });
  return (
    <div className="admin-card-grid">
      <section className="card">
        <Copy aria-hidden="true" />
        <h2>Duplicate event</h2>
        <p className="muted">Create a complete independent copy, including current assignments.</p>
        <button
          className="subtle"
          disabled={copyEvent.isPending}
          onClick={() => copyEvent.mutate()}
        >
          {copyEvent.isPending ? 'Duplicating…' : 'Duplicate event'}
        </button>
      </section>
      <section className="card danger-zone">
        <ShieldAlert aria-hidden="true" />
        <h2>Delete event</h2>
        <p>This permanently removes the event and all retained version history.</p>
        <button
          className="danger"
          disabled={deleteEvent.isPending}
          onClick={() => {
            if (
              confirm(
                `Delete "${event.data?.event.name ?? 'this event'}" and all version history? This is permanent.`,
              )
            ) {
              deleteEvent.mutate();
            }
          }}
        >
          <Trash2 aria-hidden="true" />
          Delete event
        </button>
      </section>
    </div>
  );
}

function AdminCard({
  icon: Icon,
  title,
  description,
  to,
}: {
  icon: typeof Settings;
  title: string;
  description: string;
  to: string;
}) {
  return (
    <Link className="card admin-card" to={to}>
      <Icon aria-hidden="true" />
      <h2>{title}</h2>
      <p className="muted">{description}</p>
    </Link>
  );
}

function parseInstructionLines(value: string): string[] {
  return value
    .split('\n')
    .map((instruction) => instruction.trim())
    .filter(Boolean);
}

function changeCount(version: EventVersionSummary): number {
  return Object.values(version.diff).reduce(
    (total, diff) => total + diff.added + diff.updated + diff.deleted,
    0,
  );
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
